import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import addDays from "@insite/client-framework/Common/Utilities/addDays";
import Zone from "@insite/client-framework/Components/Zone";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import setIsPreloadingData from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetIsPreloadingData";
import setRequestedDeliveryDate from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetRequestedDeliveryDate";
import setRequestedPickUpDate from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetRequestedPickUpDate";
import setShippingMethod from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetShippingMethod";
import translate from "@insite/client-framework/Translate";
import { CarrierDto, ShipViaDto } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import MultiSelectDropdown, { Option } from "@tKgpco/Components/MultiSelectDropdown";
import loadShipViaAccessorials from "@tKgpco/Handlers/Accessorials/LoadShipViaAccessorials";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import setAccountNumber from "@tKgpco/Store/Data/CheckoutReviewAndSubmit/Handlers/SetAccountHandler";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import { useWORContext } from "@tKgpco/Widgets/WOR/WORCheckoutReviewSubmitPage";
import React, { ChangeEvent, FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomApplicationState) => {
    const settingsCollection = getSettingsCollection(state);
    const { session } = state.context;
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    const fulfillmentMethod = cart?.fulfillmentMethod || session.fulfillmentMethod;
    return {
        cart,
        showShippingMethod: fulfillmentMethod === FulfillmentMethod.Ship,
        showPickUpDate:
            settingsCollection.accountSettings.enableWarehousePickup &&
            settingsCollection.cartSettings.enableRequestPickUpDate &&
            fulfillmentMethod === FulfillmentMethod.PickUp,
        session,
        accountSettings: settingsCollection.accountSettings,
        cartSettings: settingsCollection.cartSettings,
        allowEmptyShipping: settingsCollection.accountSettings.allowEmptyShipping,
        billToState: cart?.billToId ? getBillToState(state, cart?.billToId) : null,
        accessorials: state.KGPState.customAccessorials.carrierShipAccessorial,
        defaultDeliveryDate: state.context.settings.properties.defaultDaysToAddForRequestedDeliveryDate,
        isAwaitingApproval: cart?.isAwaitingApproval,
        isShippingServicesDisabled: state?.context?.session?.properties?.disableSpecialShippingServices === "true",
    };
};

const mapDispatchToProps = {
    setShippingMethod,
    setRequestedDeliveryDate,
    setRequestedPickUpDate,
    setIsPreloadingData,
    setAccountNumber,
    loadShipViaAccessorials,
};

type Props = WidgetProps & OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CheckoutShippingCarrierServiceStyles {
    container?: GridContainerProps;
    centeringWrapper?: InjectableCss;
    noCarriersFoundGridItem?: GridItemProps;
    noCarriersFoundText?: TypographyPresentationProps;
    carrierGridItem?: GridItemProps;
    shippingMethod?: TypographyPresentationProps;
    shippingMethodRequiredLabel?: TypographyPresentationProps;
    carrierSelect?: SelectPresentationProps;
    serviceGridItem?: GridItemProps;
    shippingAccountGridItem?: GridItemProps;
    serviceSelect?: SelectPresentationProps;
    deliveryDateGridItem?: GridItemProps;
    deliveryDatePicker?: DatePickerPresentationProps;
    pickUpDateGridItem?: GridItemProps;
    pickUpDatePicker?: DatePickerPresentationProps;
    shippingInformationTitleGridItem?: GridItemProps;
    hazardousInfoText?: GridItemProps;
    accountShippingTermGrid?: GridItemProps;
    warningSectionCustom?: InjectableCss;
    warningIconCustom?: IconPresentationProps;
    warningTextCustom?: InjectableCss;
    shippingAccountLabel?: TypographyPresentationProps;
    shippingAccountNumber?: TypographyPresentationProps;
    shippingAccountNumberChange?: TypographyPresentationProps;
}

export const checkoutShippingCarrierServiceStyles: CheckoutShippingCarrierServiceStyles = {
    container: {
        gap: 20,
        css: css`
            ${GridOffset} {
                width: calc(100% + 15px) !important;
            }
        `,
    },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 450px;
        `,
    },
    noCarriersFoundGridItem: { width: 12 },
    carrierGridItem: {
        width: [6, 6, 6, 6, 6],
        css: css`
            label {
                font-size: ${TYPOGRAPHY.size.smallest};
            }
        `,
    },
    shippingInformationTitleGridItem: {
        width: 12,
        css: css`
            overflow: visible;
            display: flex;
            flex-direction: row;
            align-items: flex-start;
            overflow-wrap: break-word;
            word-break: break-word;
            flex-basis: 100%;
            max-width: 100%;
            background: ${COLORS.background.shade};
            padding: 14px;
            border-bottom: 2px solid rgb(30, 117, 186);
            border-radius: 8px 8px 0px 0px;
            justify-content: space-between;
            align-items: center;
        `,
    },
    shippingMethod: {
        variant: "h4",
        css: css`
            margin-bottom: 0;
        `,
    },
    shippingMethodRequiredLabel: {
        variant: "p",
        size: 13,
    },
    hazardousInfoText: {
        width: 12,
        css: css`
            font-size: 11px;
        `,
    },
    serviceGridItem: {
        width: [6, 6, 6, 6, 6],
        css: css`
            label {
                font-size: ${TYPOGRAPHY.size.smallest};
            }
        `,
    },
    shippingAccountGridItem: {
        width: [6, 6, 6, 6, 6],
        css: css`
            flex-direction: column;
            label {
                font-size: ${TYPOGRAPHY.size.smallest};
            }
        `,
    },
    deliveryDateGridItem: { width: [12, 12, 12, 6, 6] },
    pickUpDateGridItem: { width: [12, 12, 12, 6, 6] },
    accountShippingTermGrid: { width: 12 },
    warningSectionCustom: {
        css: css`
            display: flex;
            align-items: flex-start;
            border: 2px solid #f0b003;
            padding: 10px 10px;
            width: 100%;
            margin: 15px;
            .errorList {
                list-style-type: disc;
                margin-left: 1rem;
                li {
                    color: #f0b003;
                }
            }
        `,
    },
    warningTextCustom: {
        css: css`
            font-size: 15px;
            color: #f0b003;
            font-weight: ${TYPOGRAPHY.weight.bold};
        `,
    },
    shippingAccountLabel: {
        weight: 600,
        size: 13,
        css: css`
            display: block;
            margin-bottom: 5px;
        `,
    },
    shippingAccountNumber: {
        size: 14,
    },
    shippingAccountNumberChange: {
        size: 14,
        color: "primary",
        weight: "bold",
        css: css`
            text-decoration: underline;
            cursor: pointer;
            margin-left: 25px;
        `,
    },
};

/**
 * @deprecated Use checkoutShippingCarrierServiceStyles instead.
 */
export const checkoutShippingCarrierService = checkoutShippingCarrierServiceStyles;
const styles = checkoutShippingCarrierServiceStyles;

const CheckoutReviewAndSubmitCarrierService: FC<Props> = ({
    cart,
    cartSettings,
    setRequestedDeliveryDate,
    setRequestedPickUpDate,
    showShippingMethod,
    setShippingMethod,
    showPickUpDate,
    setIsPreloadingData,
    id,
    allowEmptyShipping,
    billToState,
    setAccountNumber,
    loadShipViaAccessorials,
    accessorials,
    defaultDeliveryDate,
    isAwaitingApproval,
    isShippingServicesDisabled,
}) => {
    const [selectedCarrier, setSelectedCarrier] = useState<CarrierDto | null>(null);
    const [selectedShipVia, setSelectedShipVia] = useState<ShipViaDto | null>(null);
    const [accountcollectionNumbers, setAccountCollectionNumbers] = useState<string[] | null>(null);
    const [currentShippingAccountNumber, setCurrentShippingAccountNumber] = useState("");
    const [showAllAccountNumbers, setShowAllAccountNumbers] = useState(false);
    const [selectedAccessorials, setSelectedAccessorials] = useState<Option[]>([]);

    useEffect(() => {
        const allSeletedAccessorials = localStorage.getItem("checkoutAccessorialCompleted");
        if (allSeletedAccessorials) {
            setSelectedAccessorials(JSON.parse(allSeletedAccessorials));
        }
    }, []);

    useEffect(() => {
        if (cart?.properties?.selectedAccessorialsDisplay) {
            setSelectedAccessorials(JSON.parse(cart.properties.selectedAccessorialsDisplay));
        }
    }, [cart]);

    useEffect(() => {
        if (cart) {
            // setSelectedAccessorials([]);
            setSelectedCarrier(cart.carrier);
            setSelectedShipVia(cart.shipVia);
        }
    }, [cart?.carrier, cart?.shipVia]);

    useEffect(() => {
        if (selectedShipVia?.id && selectedCarrier?.id && !isShippingServicesDisabled) {
            loadShipViaAccessorials({
                shipViaId: selectedShipVia?.id || "",
                carrierId: selectedCarrier?.id || "",
            });
        }
    }, [selectedCarrier, selectedShipVia, isShippingServicesDisabled]);

    useEffect(() => {
        const selectedAccessorial = selectedAccessorials?.map(item => item.value).join("|");
        localStorage.setItem("checkoutAccessorialCompleted", JSON.stringify(selectedAccessorials));
        localStorage.setItem("checkoutAccessorial", selectedAccessorial);
    }, [selectedAccessorials]);

    useEffect(() => {
        setAccountNumber({ accountNumber: currentShippingAccountNumber });
    }, [currentShippingAccountNumber]);

    useEffect(() => {
        if (billToState?.value && billToState.value.properties.collectAccountNumber && selectedCarrier) {
            setAccountCollectionNumbers(
                billToState.value.properties.collectAccountNumber
                    .split("|")
                    .filter(accounts => accounts.includes(selectedCarrier.description)),
            );
        }
    }, [billToState, selectedCarrier]);

    useEffect(() => {
        if (cart && cart?.type === "Quote") {
            setRequestedDeliveryDate({
                requestedDeliveryDate: addDays(
                    new Date(),
                    Number(billToState?.value?.properties?.daysToAddForRequestedDeliveryDate) ||
                        Number(defaultDeliveryDate),
                ),
                requestedDeliveryDateDisabled: false,
            });
        }
    }, [billToState?.value?.properties, defaultDeliveryDate, cart]);

    useEffect(() => {
        if (accountcollectionNumbers?.length && selectedCarrier?.description) {
            setCurrentShippingAccountNumber(accountcollectionNumbers[0].split("_")[0]);
        } else {
            setCurrentShippingAccountNumber("");
        }
    }, [accountcollectionNumbers, selectedCarrier]);

    if (!cart || (cart.lineCount && (!cart.cartLines || cart.cartLines.length === 0)) || !cart.carriers) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner data-test-selector="checkoutShipping_carrierServiceLoading" />
            </StyledWrapper>
        );
    }

    const carrierChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
        const selectedCarrier = cart.carriers!.find(c => c.id === event.currentTarget.value)!;
        const defaultShipVias = selectedCarrier.shipVias!.filter(o => o.isDefault);
        const shipVia = defaultShipVias.length > 0 ? defaultShipVias[0] : selectedCarrier.shipVias![0];
        setIsPreloadingData({ isPreloadingData: true });
        setSelectedCarrier(selectedCarrier);
        setShowAllAccountNumbers(false);
        setSelectedShipVia(selectedCarrier.shipVias![0]);
        setShippingMethod({
            carrier: selectedCarrier,
            shipVia: selectedCarrier.shipVias![0],
            onComplete: () => {
                setIsPreloadingData({ isPreloadingData: false });
            },
        });
    };
    const shipViaChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
        if (!selectedCarrier) {
            return;
        }
        const selectedShipVia = selectedCarrier.shipVias!.find(s => s.id === event.currentTarget.value)!;
        setIsPreloadingData({ isPreloadingData: true });
        setSelectedShipVia(selectedShipVia);
        setShippingMethod({
            carrier: selectedCarrier,
            shipVia: selectedShipVia,
            onComplete: () => {
                setIsPreloadingData({ isPreloadingData: false });
            },
        });
    };

    const setShippingAccountNumber = (event: ChangeEvent<HTMLSelectElement>) => {
        if (event.target.value) {
            setCurrentShippingAccountNumber(event.target.value);
        } else {
            setCurrentShippingAccountNumber("");
        }
    };

    const handleRequestPickUpDateChanged = ({ selectedDay, selectedDayDisabled }: DatePickerState) => {
        setRequestedPickUpDate({
            requestedPickUpDate: selectedDay,
            requestedPickUpDateDisabled: selectedDayDisabled,
        });
    };
    const handleRequestDeliveryDateChanged = ({ selectedDay, selectedDayDisabled }: DatePickerState) => {
        setRequestedDeliveryDate({
            requestedDeliveryDate: selectedDay,
            requestedDeliveryDateDisabled: selectedDayDisabled,
        });
    };
    const StyledSection = getStyledWrapper("section");
    const isOverLengthProduct =
        cart?.cartLines?.filter((item: any) => item?.properties.isOverlength?.toLowerCase() === "true") ?? [];

    const handleDropdownChange = (option: Option) => {
        const isSelected =
            selectedAccessorials &&
            selectedAccessorials.length > 0 &&
            selectedAccessorials.filter(val => val?.value.indexOf(option?.value) !== -1).length > 0;

        let status: Option[] = selectedAccessorials && selectedAccessorials.length > 0 ? [...selectedAccessorials] : [];
        if (isSelected) {
            status = selectedAccessorials!.filter(val => option.value !== val.value);
        } else {
            status.push(option);
        }
        if (status.length > 0) {
            setSelectedAccessorials(status);
        } else {
            setSelectedAccessorials([]);
        }
    };

    const options = accessorials || [];
    const uniqueOptions = new Map<string, string[]>();
    for (const option of options) {
        const accessorialsOption = uniqueOptions.get(option.name) || [];
        accessorialsOption.push(option.master);
        uniqueOptions.set(option.name, accessorialsOption);
    }

    const dropDownOptions: Option[] = Array.from(uniqueOptions, ([name, master]) => ({ name, master })).map(option => ({
        value: option.master.join(","),
        label: option.name,
    }));

    const selectedOptions: Option[] =
        selectedAccessorials && selectedAccessorials.length > 0
            ? selectedAccessorials.map(val => ({
                  value: val.value,
                  label: dropDownOptions.find(opt => opt.value === val.value)?.label || "",
              }))
            : [];

    return (
        <GridContainer {...styles.container}>
            {isOverLengthProduct.length > 0 && (
                <StyledSection {...styles.warningSectionCustom}>
                    <Icon {...styles.warningIconCustom}></Icon>
                    <Typography {...styles.warningTextCustom}>{siteMessage("overlength-wor-message")}</Typography>
                </StyledSection>
            )}
            {showShippingMethod && (
                <>
                    {cart.carriers!.length === 0 && !allowEmptyShipping && (
                        <GridItem {...styles.noCarriersFoundGridItem}>
                            <Typography {...styles.noCarriersFoundText}>
                                {siteMessage("ReviewAndPay_NoCarriersFound")}
                            </Typography>
                        </GridItem>
                    )}
                    {cart.carriers!.length > 0 && (
                        <>
                            <GridItem {...styles.shippingInformationTitleGridItem}>
                                <Typography as="h4" {...styles.shippingMethod}>
                                    {translate("Shipping method")}
                                </Typography>
                                <Typography as="h5" {...styles.shippingMethodRequiredLabel}>
                                    <span style={{ color: "red" }}>*</span>
                                    {translate("Required")}
                                </Typography>
                            </GridItem>
                            <GridItem {...styles.hazardousInfoText}>
                                <Typography as="p">{siteMessage("hazardous_InfoText")}</Typography>
                            </GridItem>
                            <GridItem {...styles.hazardousInfoText}>
                                <Typography as="p">{siteMessage("premium_ShippingInfo")}</Typography>
                            </GridItem>
                            <GridItem {...styles.accountShippingTermGrid}>
                                <Zone contentId={id} zoneName="accountShippingTerm" />
                            </GridItem>
                            <GridItem {...styles.carrierGridItem}>
                                <Select
                                    label={translate("Select Carrier")}
                                    {...styles.carrierSelect}
                                    value={selectedCarrier?.id || ""}
                                    onChange={carrierChangeHandler}
                                    data-test-selector="checkoutReviewAndSubmit_carrierSelect"
                                    name="carrier"
                                    required
                                    disabled={isAwaitingApproval}
                                >
                                    {cart
                                        .carriers!.filter(carrier => {
                                            if (
                                                carrier?.description?.includes("Expedited") &&
                                                isOverLengthProduct.length > 0
                                            ) {
                                                return false;
                                            }
                                            return true;
                                        })
                                        .map(c => {
                                            const id = c.id!.toString();
                                            return (
                                                <option key={id} value={id}>
                                                    {c.description}
                                                </option>
                                            );
                                        })}
                                </Select>
                            </GridItem>
                            <GridItem {...styles.serviceGridItem}>
                                <Select
                                    label={translate("Select Service")}
                                    {...styles.serviceSelect}
                                    value={selectedShipVia?.id || ""}
                                    onChange={shipViaChangeHandler}
                                    data-test-selector="checkoutReviewAndSubmit_shippingServiceSelect"
                                    required
                                    disabled={isAwaitingApproval}
                                >
                                    {selectedCarrier &&
                                        selectedCarrier.shipVias!.map(s => {
                                            const id = s.id.toString();
                                            return (
                                                <option key={id} value={id}>
                                                    {s.description}{" "}
                                                    {cart?.properties?.shippingPriority
                                                        ? `(${cart.properties.shippingPriority})`
                                                        : ""}
                                                </option>
                                            );
                                        })}
                                </Select>
                            </GridItem>
                            {accessorials?.length && !isShippingServicesDisabled ? (
                                <GridItem {...styles.serviceGridItem}>
                                    <MultiSelectDropdown
                                        label="Premium Shipping Services "
                                        handleOptionClick={handleDropdownChange}
                                        options={dropDownOptions}
                                        selectedOptions={selectedOptions}
                                        placeHolder="Select Accessorials"
                                        isDisabled={isAwaitingApproval}
                                    />
                                </GridItem>
                            ) : null}

                            {billToState?.value?.properties.deliveryTerms.toLowerCase() === "3p" ||
                            cart?.properties?.kgpBilledFreight.toLowerCase() === "true" ||
                            cart?.properties?.carrierBilledDefault.toLowerCase() === "true" ? (
                                <GridItem {...styles.shippingAccountGridItem}>
                                    <Typography {...styles.shippingAccountLabel}>
                                        {translate("Shipping Account")}
                                    </Typography>
                                    {cart?.properties?.isThirdPartyExternalAccount?.toLowerCase() === "true" ? (
                                        <Typography
                                            style={{
                                                fontWeight: "bold",
                                            }}
                                        >
                                            {siteMessage("carrier_billed")}
                                        </Typography>
                                    ) : null}
                                    {cart?.properties?.kgpBilledFreight?.toLowerCase() === "true" ? (
                                        <Typography
                                            style={{
                                                fontWeight: "bold",
                                            }}
                                        >
                                            {siteMessage("freight_charges_desc_kgp")}
                                        </Typography>
                                    ) : null}
                                    {cart?.properties?.carrierBilledDefault?.toLowerCase() === "true" ? (
                                        <Typography
                                            style={{
                                                fontWeight: "bold",
                                            }}
                                        >
                                            {siteMessage("freight_charges_desc_default")}
                                        </Typography>
                                    ) : null}
                                    {showAllAccountNumbers ? (
                                        <Select
                                            {...styles.serviceSelect}
                                            value={currentShippingAccountNumber}
                                            onChange={setShippingAccountNumber}
                                            data-test-selector="checkoutReviewAndSubmit_shippingAccountNumberSelect"
                                            disabled={isAwaitingApproval}
                                        >
                                            {accountcollectionNumbers?.length &&
                                                accountcollectionNumbers.map(account => {
                                                    return (
                                                        <option key={account} value={account.split("_")[0]}>
                                                            {account.split("_")[0]}
                                                        </option>
                                                    );
                                                })}
                                        </Select>
                                    ) : (
                                        <Typography {...styles.shippingAccountNumber}>
                                            {currentShippingAccountNumber}{" "}
                                            {accountcollectionNumbers?.length &&
                                            accountcollectionNumbers?.length > 1 ? (
                                                <Typography
                                                    {...styles.shippingAccountNumberChange}
                                                    onClick={() => setShowAllAccountNumbers(true)}
                                                >
                                                    {translate("Change")}
                                                </Typography>
                                            ) : null}
                                        </Typography>
                                    )}
                                </GridItem>
                            ) : null}
                        </>
                    )}
                    {cart.type === "Quote" && cartSettings.canRequestDeliveryDate && (
                        <GridItem
                            {...styles.deliveryDateGridItem}
                            data-test-selector="checkoutShippingRequestedDeliveryDate"
                        >
                            <DatePicker
                                required={true}
                                label={`${translate("Request Delivery Date")}`}
                                hint={siteMessage("Checkout_RequestedDeliveryDateInformation")}
                                {...styles.deliveryDatePicker}
                                selectedDay={
                                    addDays(
                                        new Date(),
                                        billToState?.value?.properties?.daysToAddForRequestedDeliveryDate &&
                                            Number(billToState?.value?.properties?.daysToAddForRequestedDeliveryDate)
                                            ? Number(billToState?.value?.properties?.daysToAddForRequestedDeliveryDate)
                                            : Number(defaultDeliveryDate),
                                    )!
                                }
                                dateTimePickerProps={{
                                    minDate: new Date(),
                                    maxDate: addDays(new Date(), cartSettings.maximumDeliveryPeriod),
                                    ...styles.deliveryDatePicker?.dateTimePickerProps,
                                }}
                                onDayChange={handleRequestDeliveryDateChanged}
                                data-test-selector="checkoutReviewAndSubmit_deliveryDate"
                            />
                        </GridItem>
                    )}
                </>
            )}
            {showPickUpDate && (
                <>
                    <GridItem {...styles.pickUpDateGridItem} data-test-selector="checkoutPickUpRequestedDeliveryDate">
                        <DatePicker
                            label={`${translate("Request Pick Up Date")} (${translate("optional")})`}
                            hint={siteMessage("Checkout_RequestedPickupDateInformation")}
                            {...styles.pickUpDatePicker}
                            selectedDay={cart.requestedPickupDateDisplay!}
                            dateTimePickerProps={{
                                minDate: new Date(),
                                maxDate: addDays(new Date(), cartSettings.maximumDeliveryPeriod),
                                ...styles.pickUpDatePicker?.dateTimePickerProps,
                            }}
                            onDayChange={handleRequestPickUpDateChanged}
                            monthAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                                "Enter month, format M M",
                            )}`}
                            dayAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                                "Enter day, format D D",
                            )}`}
                            yearAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                                "Enter year, format Y Y Y Y",
                            )}`}
                        />
                    </GridItem>
                </>
            )}
        </GridContainer>
    );
};
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CheckoutReviewAndSubmitCarrierService),
    definition: {
        displayName: "Carrier & Service",
        group: "Checkout - Review & Submit",
        allowedContexts: ["CheckoutReviewAndSubmitPage"],
    },
};
export default widgetModule;
